import Swal from 'sweetalert2';

// Circle Moments brand colors
const CM_ACCENT = '#c73866';
const CM_CANCEL = '#8a8a8a';
const CM_DANGER = '#d32f2f';

// Base mixin shared by all dialogs
const cmSwal = Swal.mixin({
  confirmButtonColor: CM_ACCENT,
  cancelButtonColor: CM_CANCEL,
  reverseButtons: true,
  focusCancel: true,
  customClass: { popup: 'cm-swal-popup' },
});

/**
 * Standard confirmation dialog.
 * Returns true if the user clicks Confirm.
 */
export async function swalConfirm({
  title,
  html,
  text,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
} = {}) {
  const { isConfirmed } = await cmSwal.fire({
    title,
    html,
    text,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
  });
  return isConfirmed;
}

/**
 * Destructive action confirmation (delete / disconnect).
 * Confirm button is red to signal irreversibility.
 * Returns true if the user clicks Confirm.
 */
export async function swalConfirmDelete({
  title,
  html,
  text,
  confirmText = 'Delete',
  cancelText = 'Cancel',
} = {}) {
  const { isConfirmed } = await cmSwal.fire({
    title,
    html,
    text,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: confirmText,
    confirmButtonColor: CM_DANGER,
    cancelButtonText: cancelText,
  });
  return isConfirmed;
}

/**
 * Text input prompt.
 * Returns the trimmed input string, or null if cancelled / empty.
 */
export async function swalPrompt({
  title,
  label,
  placeholder = '',
  inputValue = '',
  confirmText = 'OK',
  cancelText = 'Cancel',
} = {}) {
  const { isConfirmed, value } = await cmSwal.fire({
    title,
    input: 'text',
    inputLabel: label,
    inputPlaceholder: placeholder,
    inputValue,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    focusCancel: false,
    inputAttributes: { autocomplete: 'off', spellcheck: 'false' },
    preConfirm: (val) => {
      const trimmed = (val || '').trim();
      if (!trimmed) {
        Swal.showValidationMessage('Please enter a name.');
        return false;
      }
      return trimmed;
    },
  });
  return isConfirmed ? value : null;
}

/**
 * Required-checkbox consent dialog. Used for biometric/face-grouping consent
 * where a plain Confirm/Cancel isn't enough evidence of an informed,
 * affirmative attestation (e.g. PoFA 2012 minors acknowledgement).
 *
 * Returns true if the user checked the box and clicked Confirm, false otherwise.
 */
export async function swalConsentCheckbox({
  title,
  html,
  checkboxLabel,
  confirmText = 'I Confirm & Continue',
  cancelText = 'Cancel',
} = {}) {
  const { isConfirmed, value } = await cmSwal.fire({
    title,
    html,
    input: 'checkbox',
    inputValue: 0,
    inputPlaceholder: checkboxLabel,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    focusCancel: true,
    preConfirm: (checked) => {
      if (!checked) {
        Swal.showValidationMessage('Please tick the box to confirm before continuing.');
        return false;
      }
      return true;
    },
  });
  return Boolean(isConfirmed && value);
}

/**
 * Three-way choice dialog (two affirmative options + cancel), e.g. "include
 * existing items or only new ones from now on?". Returns 'confirm' if the
 * primary option was picked, 'deny' if the secondary option was picked, or
 * null if the user cancelled/dismissed.
 */
export async function swalChoice({
  title,
  html,
  text,
  confirmText = 'Yes',
  denyText = 'No',
  cancelText = 'Cancel',
} = {}) {
  const { isConfirmed, isDenied } = await cmSwal.fire({
    title,
    html,
    text,
    icon: 'question',
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: confirmText,
    denyButtonText: denyText,
    cancelButtonText: cancelText,
    reverseButtons: false,
  });
  if (isConfirmed) return 'confirm';
  if (isDenied) return 'deny';
  return null;
}

export function swalLoading({ title = 'Please wait', html = '' } = {}) {
  cmSwal.fire({
    title,
    html,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });
}

export function swalClose() {
  Swal.close();
}
